import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Phone, User, Calendar, Clock, LogOut, Navigation, CheckCircle, XCircle, PlayCircle, MessageCircle, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { bookings } from '../data/mock';
import MessagePassenger from '../components/MessagePassenger';
import { sendBookingEmail, calculateBookingFare } from '../services/emailService';

const DriverDashboard = () => {
  const navigate = useNavigate();
  const [driverBookings, setDriverBookings] = useState([]);
  const [driverInfo, setDriverInfo] = useState(null);

  useEffect(() => {
    // Check if driver is logged in
    const auth = localStorage.getItem('driverAuth');
    if (!auth) {
      toast.error('Please login to access the dashboard');
      navigate('/driver-login');
      return;
    }

    const authData = JSON.parse(auth);
    setDriverInfo(authData);

    // Filter bookings for this driver
    const assignedBookings = bookings.filter(b => b.assignedDriver === authData.username);
    setDriverBookings(assignedBookings);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('driverAuth');
    toast.success('Logged out successfully');
    navigate('/driver-login');
  };

  const updateBookingStatus = (bookingId, newStatus) => {
    const booking = driverBookings.find(b => b.id === bookingId);
    
    setDriverBookings(prev => 
      prev.map(booking => 
        booking.id === bookingId ? { ...booking, status: newStatus } : booking
      )
    );
    
    const statusMessages = {
      'in-progress': 'Trip started',
      'completed': 'Trip completed',
      'confirmed': 'Booking accepted'
    };
    
    toast.success(statusMessages[newStatus] || 'Status updated');

    // Send email notifications
    if (booking) {
      const totalFare = calculateBookingFare(booking);
      
      if (newStatus === 'confirmed') {
        sendBookingEmail(booking, 'confirmed', totalFare);
      } else if (newStatus === 'in-progress') {
        sendBookingEmail(booking, 'on-the-way', totalFare);
      } else if (newStatus === 'completed') {
        sendBookingEmail(booking, 'completed', totalFare);
      }
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'confirmed': 'bg-blue-100 text-blue-800 border-blue-300',
      'in-progress': 'bg-green-100 text-green-800 border-green-300',
      'completed': 'bg-gray-100 text-gray-800 border-gray-300'
    };

    return (
      <Badge className={`${variants[status]} border`}>
        {status.replace('-', ' ').toUpperCase()}
      </Badge>
    );
  };

  const filterBookingsByStatus = (status) => {
    if (status === 'all') return driverBookings;
    return driverBookings.filter(b => b.status === status);
  };

  const pendingBookings = filterBookingsByStatus('pending');
  const activeBookings = driverBookings.filter(b => ['confirmed', 'in-progress'].includes(b.status));
  const completedBookings = filterBookingsByStatus('completed');

  if (!driverInfo) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-black text-white shadow-lg sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_c9569150-39ed-49a0-bcf7-6c91669fb705/artifacts/k5pmqpyw_IMG_8391%202.jpg" 
                alt="RELAX & RIDE Logo" 
                className="h-12 w-12 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-red-500">Driver Dashboard</h1>
                <p className="text-sm text-gray-400">Welcome, {driverInfo.username}</p>
              </div>
            </div>
            <Button 
              onClick={handleLogout}
              variant="outline" 
              className="border-red-600 text-red-500 hover:bg-red-600 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Dashboard Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="border-l-4 border-yellow-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pending</p>
                  <p className="text-3xl font-bold">{pendingBookings.length}</p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Rides</p>
                  <p className="text-3xl font-bold">{activeBookings.length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Navigation className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Completed</p>
                  <p className="text-3xl font-bold">{completedBookings.length}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-red-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Today</p>
                  <p className="text-3xl font-bold">{driverBookings.length}</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bookings Tabs */}
        <Tabs defaultValue="all" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 max-w-2xl">
            <TabsTrigger value="all">All Rides</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="active">Active</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            <BookingsList 
              bookings={driverBookings} 
              onStatusUpdate={updateBookingStatus}
              getStatusBadge={getStatusBadge}
            />
          </TabsContent>

          <TabsContent value="pending">
            <BookingsList 
              bookings={pendingBookings} 
              onStatusUpdate={updateBookingStatus}
              getStatusBadge={getStatusBadge}
            />
          </TabsContent>

          <TabsContent value="active">
            <BookingsList 
              bookings={activeBookings} 
              onStatusUpdate={updateBookingStatus}
              getStatusBadge={getStatusBadge}
            />
          </TabsContent>

          <TabsContent value="completed">
            <BookingsList 
              bookings={completedBookings} 
              onStatusUpdate={updateBookingStatus}
              getStatusBadge={getStatusBadge}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Bookings List Component
const BookingsList = ({ bookings, onStatusUpdate, getStatusBadge }) => {
  const [selectedBooking, setSelectedBooking] = useState(null);

  if (bookings.length === 0) {
    return (
      <div className="text-center py-12">
        <XCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600 text-lg">No bookings found</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid gap-6">
        {bookings.map((booking) => {
          const totalFare = calculateBookingFare(booking);
          
          return (
            <Card key={booking.id} className="hover:shadow-xl transition-shadow border-l-4 border-red-600">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-xl">{booking.name}</CardTitle>
                    <p className="text-sm text-gray-600 mt-1">{booking.service}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {getStatusBadge(booking.status)}
                    {totalFare > 0 && (
                      <div className="flex items-center gap-1 bg-green-100 text-green-800 px-3 py-1 rounded-full">
                        <DollarSign className="w-4 h-4" />
                        <span className="font-bold">${totalFare.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Left Column - Customer & Trip Details */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-gray-600">Contact</p>
                      <a href={`tel:${booking.phone}`} className="font-medium hover:text-red-600">
                        {booking.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-gray-600">Date & Time</p>
                      <p className="font-medium">{booking.date} at {booking.time}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-gray-600">Passengers</p>
                      <p className="font-medium">{booking.passengers} {booking.passengers === 1 ? 'Person' : 'People'}</p>
                    </div>
                  </div>
                </div>

                {/* Right Column - Locations */}
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs text-green-700 font-semibold">PICKUP</p>
                        <p className="font-medium text-green-900">{booking.pickupLocation}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs text-red-700 font-semibold">DROP-OFF</p>
                        <p className="font-medium text-red-900">{booking.dropoffLocation}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3 mt-6">
                {booking.status === 'pending' && (
                  <Button 
                    onClick={() => onStatusUpdate(booking.id, 'confirmed')}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Accept Ride
                  </Button>
                )}

                {booking.status === 'confirmed' && (
                  <Button 
                    onClick={() => onStatusUpdate(booking.id, 'in-progress')}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <PlayCircle className="w-4 h-4 mr-2" />
                    Start Trip
                  </Button>
                )}

                {booking.status === 'in-progress' && (
                  <Button 
                    onClick={() => onStatusUpdate(booking.id, 'completed')}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Complete Trip
                  </Button>
                )}

                <Button 
                  onClick={() => setSelectedBooking(booking)}
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Message
                </Button>

                <Button 
                  onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(booking.pickupLocation + ', Nassau, Bahamas')}`, '_blank')}
                  variant="outline"
                  className="border-red-600 text-red-600 hover:bg-red-50"
                >
                  <Navigation className="w-4 h-4 mr-2" />
                  Navigate
                </Button>

                <Button 
                  onClick={() => window.location.href = `tel:${booking.phone}`}
                  variant="outline"
                  className="border-green-600 text-green-600 hover:bg-green-50"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Call
                </Button>
              </div>
            </CardContent>
          </Card>
        );
        })}
      </div>

      {/* Message Modal */}
      {selectedBooking && (
        <MessagePassenger 
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
        />
      )}
    </>
  );
};

export default DriverDashboard;
